'use strict';

var _ = require('lodash');

var MapStoreFactory = function ($q, leafletData, Flux, MapActions, LayerUtil, DISPATCHER_ID) {

  var Store = Flux.Store;
  var DispatcherUtil = Flux.Dispatcher;

  var events = {
    Changed: true,
    Loaded: true,
    ChangedActiveLayer: true,
    ChangedPopupState: true,
    EditedActiveLayer: true,
    ChangedEditableTypes: true
  };

  var state = {
    activeLayer: null, // raw active layer (annotation layer, mission zone layer, etc)
    activeLayerPopupOpen: false, // whether the active layer popup is open
    activeLayerPopupContent: null, // content to display when the popup is open
    dcOpacity: null,
    editableTypes: null,
    layers: {}, // rendered layers (grouped by collection)
    mapOpacity: null
  };

  // ## Instance Methods
  var MapStore = Store.extend({

    // init will be run by the store constructor
    init: function (definition) {
      this.mapId = definition.mapId;
      this.registerWatchers();
      this.registerWithDispatcher();
    },

    registerWatchers: function () {
      this.watch('activeLayer', this.Events.Changed);
      this.watch('activeLayerPopupOpen', this.Events.Changed);
      this.watch('activeLayerPopupOpen', this.Events.ChangedPopupState);
      this.watch('editableTypes', this.Events.ChangedEditableTypes);
    },

    registerWithDispatcher: function () {
      var mapId = this.mapId;
      var d = DispatcherUtil.get(DISPATCHER_ID);
      var Actions = MapActions.get(mapId);

      d.register(Actions.EditedActiveLayer, this.onEditedActiveLayer.bind(this));
      d.register(Actions.SetActiveLayer, this.onSetActiveLayer.bind(this));
      d.register(Actions.UnsetActiveLayer, this.onUnsetActiveLayer.bind(this));
      d.register(Actions.CloseActiveLayerPopup, this.onCloseActiveLayerPopup.bind(this));
      d.register(Actions.OpenActiveLayerPopup, this.onOpenActiveLayerPopup.bind(this));
      d.register(Actions.SetActiveLayerPopupContent, this.onSetActiveLayerPopupContent.bind(this));
      d.register(Actions.SetEditableTypes, this.onSetEditableTypes.bind(this));
      d.register(Actions.SetOpacityValues, this.onSetOpacityValues.bind(this));

      // Keep track of wrapped layers
      d.register(Actions.RegisterLayer, this.onRegisterLayer.bind(this));
      d.register(Actions.ResetLayers, this.onResetLayers.bind(this));
    },

    getMap: function () {
      var id = this.mapId;
      var deferred = $q.defer();

      leafletData.getMap(id).then(function (map) {
        map.whenReady(function () {
          deferred.resolve(map);
        });
      });

      return deferred.promise;
    },

    // Wrapper Layer Handlers
    onRegisterLayer: function (action) {
      var layer = action.layer;
      var collection = action.collection; // collection name
      var layers = this.get('layers');

      if (!layers[collection]) {
        layers[collection] = [];
      }

      layers[collection].push(layer);
      this.$set('layers', layers);
    },

    onResetLayers: function (action) {
      var layers = this.get('layers');
      var collection = action.collection; // collection name

      delete layers[collection];
      this.$set('layers', layers);
    },

    // Popup Handlers

    onCloseActiveLayerPopup: function (action) {
      var layer = action.layer;
      var active = this.get('activeLayer', false);

      // only close if there is no active OR the layer dispatching this action
      // is the same as the currently active layer
      if (!active || this._isSameLayer(active, layer)) {
        this.$set('activeLayerPopupOpen', false);
        this.$set('activeLayerPopupContent', null);
      }
    },

    onOpenActiveLayerPopup: function (action) {
      this.$set('activeLayerPopupOpen', true);
    },

    onSetActiveLayerPopupContent: function (action) {
      this.$set('activeLayerPopupContent', action.content);
    },

    // Active Layer Handlers

    onEditedActiveLayer: function (action) {
      var wlayer = action.wlayer;

      // transform wlayer into rlayer
      var transform = this._getTransform(wlayer);
      if (transform) {
        // update rlayer data and set to active
        var rlayer = transform.formatLayer(wlayer);

        // using false so that we don't trigger a watch when we set it
        var activeLayer = this.get('activeLayer', false);
        activeLayer = _.extend(activeLayer, rlayer);
        this.$set('activeLayer', activeLayer);
        this.emit(this.Events.EditedActiveLayer);
      }
    },

    onSetActiveLayer: function (action) {
      var layer = action.layer;
      var active = this.get('activeLayer');

      this.$set('activeLayer', action.layer);
      this.$set('activeLayerPopupOpen', true);

      if (!active || (active && !this._isSameLayer(active, layer))) {
        this.emit(this.Events.ChangedActiveLayer);
      }
    },

    onUnsetActiveLayer: function (action) {
      var active = this.get('activeLayer');

      if (active && this._isSameLayer(active, action.layer)) {
        this.$set('activeLayer', null);
        this.$set('activeLayerPopupOpen', false);
        this.$set('activeLayerPopupContent', null);
        this.emit(this.Events.ChangedActiveLayer);
      }
    },

    onSetEditableTypes: function (action) {
      this.$set('editableTypes', action.types);
    },

    onSetOpacityValues: function (payload) {
      this.$set('dcOpacity', payload.opacities.dcOpacity);
      this.$set('mapOpacity', payload.opacities.mapOpacity);
    },

    // Helpers

    _getTransform: function () {
      // NOTE: We can use the base transform rather than the subclasses because
      // currently all of the subclasses `formatLayer` using the default
      return LayerUtil.Transform.create();
    },

    _isSameLayer: function (activeLayer, layer) {

      if (!activeLayer) {
        return false;
      }

      if (activeLayer.$$layerUID !== undefined &&
          layer.$$layerUID !== undefined) {
        return activeLayer.$$layerUID === layer.$$layerUID;
      }
      else if (activeLayer.id !== undefined &&
          layer.id !== undefined) {
        // All layers should have a type now...
        if (activeLayer.type && layer.type) {
          return activeLayer.id === layer.id && activeLayer.type === layer.type;
        }
        // But just in case they don't...
        else {
          console.warn('Layer is missing a type property. Is this intentional?');
          return activeLayer.id === layer.id;
        }
      } else {
        return false;
      }
    }
  });

  function getName (mapId) {
    return ['SBMap', mapId, 'Store'].join('');
  }

  return {
    create: function (mapId) {
      var store = MapStore.create({
        mapId: mapId,
        name: getName(mapId),
        events: events,
        state: state
      });

      return store;
    },

    get: function (mapId) {
      var name = getName(mapId);
      return Store.get(name);
    }
  };
};

MapStoreFactory.$inject = [
'$q',
'leafletData',
'sbFlux',
'sbMapActions',
'sbMapLayerUtil',
'SB_MAP_DISPATCHER_ID'
];

module.exports = MapStoreFactory;



// WEBPACK FOOTER //
// ./~/@skycatch/skydash-saturn/boosters/map/src/app/MapStore.js